import React, { useState } from "react";
import BarChartIcon from "@mui/icons-material/BarChart";
import TableChartIcon from "@mui/icons-material/TableChart";
import "./CostResults.css";
import { TYPE_LABELS, TYPE_ORDER } from "../../../conts/simulationConstants";

const fmt = (n, d = 0) =>
  Number(n ?? 0).toLocaleString("fr-FR", { maximumFractionDigits: d });
const eur = (n) => `${fmt(n)} €`;
const eurCompact = (n) =>
  new Intl.NumberFormat("fr-FR", {
    style: "currency",
    currency: "EUR",
    notation: "compact",
    maximumFractionDigits: 1,
  }).format(Number(n ?? 0));
const pct = (part, total) => (total > 0 ? (part / total) * 100 : 0);
const clamp = (n) => Math.min(Math.max(n, 0), 100);

const TABS = [
  { id: "env", label: "Impact environnemental" },
  { id: "res", label: "Moyens d’intervention" },
];

/* The three areas. `key` = field in the API values, `id` = css modifier. */
const ZONES = [
  { id: "menace", key: "menace", label: "Menacé" },
  { id: "saved", key: "preserve", label: "Préservé" },
  { id: "burned", key: "sinistre", label: "Sinistré" },
];

/* A price is flagged only when it does not come from the user */
const SourceTag = ({ source }) =>
  source && source !== "user" ? <em className="cr-tag">défaut</em> : null;

/* ------------------------- tab 1: environmental ------------------------- */
const buildEnvRows = (money) => {
  const { zones, forest_types: classes = [], buildings, co2 } = money;
  const rows = [];

  rows.push({
    key: "total",
    kind: "total",
    label: "Total",
    values: {
      menace: zones.menace.total_eur,
      preserve: zones.preserve.total_eur,
      sinistre: zones.sinistre.total_eur,
    },
  });

  rows.push({
    key: "forest",
    kind: "group",
    label: "Forêt",
    values: {
      menace: zones.menace.forest_eur,
      preserve: zones.preserve.forest_eur,
      sinistre: zones.sinistre.forest_eur,
    },
  });

  classes.forEach((c) =>
    rows.push({
      key: `forest-${c.class}`,
      kind: "child",
      label: c.label,
      unit: `${fmt(c.price_eur_per_ha)} €/ha`,
      source: c.source,
      values: c.eur,
    }),
  );

  rows.push({
    key: "buildings",
    kind: "group",
    label: "Bâtiments",
    unit: `${fmt(buildings.price_eur_per_m2)} €/m²`,
    source: buildings.source,
    values: buildings.eur,
  });

  rows.push({
    key: "co2",
    kind: "group",
    label: "CO₂",
    unit: `${fmt(co2.price_eur_per_t)} €/t`,
    source: co2.source,
    values: co2.eur,
    subs: {
      menace: `${fmt(co2.t.menace)} t`,
      preserve: `${fmt(co2.t.preserve)} t`,
      sinistre: `${fmt(co2.t.sinistre)} t`,
    },
  });

  return rows;
};

/* ---- table view ---- */
const EnvTable = ({ rows }) => (
  <div className="cr-table-wrap">
    <table className="cr-table">
      <thead>
        <tr>
          <th>Catégorie</th>
          <th>Prix unitaire</th>
          {ZONES.map((z) => (
            <th key={z.key}>
              <i className={`cr-dot cr-dot--${z.id}`} /> {z.label}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => (
          <tr key={r.key} className={`is-${r.kind}`}>
            <td>{r.label}</td>
            <td className="cr-unit">
              {r.unit ?? ""}
              <SourceTag source={r.source} />
            </td>
            {ZONES.map((z) => (
              <td key={z.key}>
                {eur(r.values[z.key])}
                {r.subs && <small>{r.subs[z.key]}</small>}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

/* ---- chart view ---- */
const Bar = ({ width, focus }) => (
  <div className="cr-bar">
    <div
      className={`cr-bar__fill cr-bar__fill--${focus}`}
      style={{ width: `${clamp(width)}%` }}
    />
  </div>
);

/* Every bar is measured against a reference of the SAME area:
   - Total row            -> full bar (it is the reference)
   - Forêt / Bâtiments / CO₂ -> share of the area's total
   - Forest classes       -> share of the area's Forêt subtotal
   So switching the legend filter never mixes areas. */
const EnvChart = ({ rows, focus }) => {
  const zone = ZONES.find((z) => z.id === focus);
  const { key } = zone;
  const totalValue = rows.find((r) => r.kind === "total")?.values[key] ?? 0;
  const forestValue = rows.find((r) => r.key === "forest")?.values[key] ?? 0;

  return rows.map((r) => {
    const value = r.values[key];

    let share;
    let caption = null;
    if (r.kind === "total") {
      share = totalValue > 0 ? 100 : 0;
    } else if (r.kind === "child") {
      share = pct(value, forestValue);
      caption = `${fmt(share, 1)} % de la forêt`;
    } else {
      share = pct(value, totalValue);
      caption = `${fmt(share, 1)} % du ${zone.label.toLowerCase()}`;
    }

    return (
      <div key={r.key} className={`cr-row is-${r.kind}`}>
        <span className="cr-row__label">
          <span className="cr-row__name">{r.label}</span>
          {r.unit && (
            <small>
              {r.unit}
              <SourceTag source={r.source} />
            </small>
          )}
        </span>
        <Bar width={share} focus={focus} />
        <b className="cr-row__value">
          {eur(value)}
          {caption && <small>{caption}</small>}
        </b>
      </div>
    );
  });
};

/* ------------------------- tab 2: intervention -------------------------- */
const ResourcesTable = ({ ressources }) => {
  const total = ressources?.total_eur ?? 0;
  const groups = [...(ressources?.by_type ?? [])]
    .filter((g) => g.items?.length)
    .sort((a, b) => TYPE_ORDER.indexOf(a.type) - TYPE_ORDER.indexOf(b.type));

  if (groups.length === 0) {
    return <p className="cr-empty">Aucun moyen engagé n’a été renseigné.</p>;
  }

  const shareCell = (value) => (
    <td className="cr-share">
      <div className="cr-share__wrap">
        <div className="cr-share__bar">
          <div style={{ width: `${pct(value, total)}%` }} />
        </div>
        <span>{fmt(pct(value, total), 1)} %</span>
      </div>
    </td>
  );

  return (
    <div className="cr-table-wrap">
      <table className="cr-table">
        <thead>
          <tr>
            <th>Moyen</th>
            <th>Quantité</th>
            <th>Coût / unité</th>
            <th>Coût total</th>
            <th>Part du total</th>
          </tr>
        </thead>
        <tbody>
          {groups.map((group) => (
            <React.Fragment key={group.type}>
              <tr className="is-group">
                <td colSpan={3}>{TYPE_LABELS[group.type] ?? group.type}</td>
                <td>
                  <b>{eur(group.total_eur)}</b>
                </td>
                {shareCell(group.total_eur)}
              </tr>

              {group.items.map((it, i) => {
                const itemTotal =
                  it.total_eur ?? it.units * it.value_eur_per_unit;
                return (
                  <tr
                    className="is-child"
                    key={`${group.type}-${it.name}-${i}`}
                  >
                    <td>{it.name}</td>
                    <td>{fmt(it.units)}</td>
                    <td>{eur(it.value_eur_per_unit)}</td>
                    <td>{eur(itemTotal)}</td>
                    {shareCell(itemTotal)}
                  </tr>
                );
              })}
            </React.Fragment>
          ))}
        </tbody>
        <tfoot>
          <tr className="is-total">
            <td colSpan={3}>Total</td>
            <td>{eur(total)}</td>
            <td className="cr-share">100 %</td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
};

/* ------------------------------ component ------------------------------- */
const CostResults = ({ money }) => {
  const [tab, setTab] = useState("env");
  const [view, setView] = useState("table"); // "chart" | "table"
  const [focus, setFocus] = useState("menace"); // "menace" | "saved" | "burned"

  if (!money) return null;

  const saved = money.zones.preserve.total_eur;
  const intervention = money.ressources?.total_eur ?? 0;

  const isEnv = tab === "env";
  const showTable = isEnv && view === "table";

  return (
    <div className="cr">
      <div className="cr-stats">
        <div className="cr-stat">
          <small>Valeur sauvée</small>
          <strong>{eurCompact(saved)}</strong>
          <span>{eur(saved)}</span>
        </div>
        <div className="cr-stat">
          <small>Coût d’intervention</small>
          <strong>{eurCompact(intervention)}</strong>
          <span>{eur(intervention)}</span>
        </div>
      </div>

      <div className="cr-tabs" role="tablist">
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            role="tab"
            aria-selected={tab === t.id}
            className={`cr-tab ${tab === t.id ? "is-active" : ""}`}
            onClick={() => setTab(t.id)}
          >
            {t.label}
          </button>
        ))}
      </div>

      <section className="cr-card">
        {isEnv && (
          <div className="cr-card__top">
            {!showTable ? (
              <div
                className="cr-legend"
                role="radiogroup"
                aria-label="Filtrer par zone"
              >
                {ZONES.map((z) => (
                  <button
                    key={z.id}
                    type="button"
                    role="radio"
                    aria-checked={focus === z.id}
                    className={`cr-legend__item ${focus === z.id ? "is-active" : ""}`}
                    onClick={() => setFocus(z.id)}
                  >
                    <i className={`cr-dot cr-dot--${z.id}`} />
                    {z.label}
                  </button>
                ))}
              </div>
            ) : (
              <span />
            )}

            <div
              className="cr-toggle"
              role="group"
              aria-label="Mode d’affichage"
            >
              <button
                type="button"
                className={view === "chart" ? "is-active" : ""}
                aria-pressed={view === "chart"}
                aria-label="Afficher le graphique"
                title="Graphique"
                onClick={() => setView("chart")}
              >
                <BarChartIcon fontSize="small" />
              </button>
              <button
                type="button"
                className={view === "table" ? "is-active" : ""}
                aria-pressed={view === "table"}
                aria-label="Afficher le tableau"
                title="Tableau"
                onClick={() => setView("table")}
              >
                <TableChartIcon fontSize="small" />
              </button>
            </div>
          </div>
        )}

        {isEnv ? (
          showTable ? (
            <EnvTable rows={buildEnvRows(money)} />
          ) : (
            <EnvChart rows={buildEnvRows(money)} focus={focus} />
          )
        ) : (
          <ResourcesTable ressources={money.ressources} />
        )}
      </section>
    </div>
  );
};

export default CostResults;